import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';

// Get API URL: z HTML (api-config), z Vite import.meta.env.VITE_API_URL, na końcu fallback.
function getApiBaseUrlFromConfig(): string {
  if (typeof window === 'undefined') return '';
  try {
    const el = document.getElementById('api-config');
    if (el?.textContent) {
      const config = JSON.parse(el.textContent) as { apiBaseUrl?: string };
      if (config.apiBaseUrl && typeof config.apiBaseUrl === 'string') return config.apiBaseUrl;
    }
  } catch {
    // ignore
  }
  return '';
}
const API_BASE_URL =
  getApiBaseUrlFromConfig() ||
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_URL) ||
  '/api/v1';

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
export const getAccessToken = (): string | null => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const setTokens = (access: string, refresh: string): void => {
  localStorage.setItem(ACCESS_TOKEN_KEY, access);
  localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
};

export const clearTokens = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor - handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    // If 401 and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = getRefreshToken();
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh/`, {
            refresh: refreshToken,
          });

          const { access, refresh } = response.data;
          setTokens(access, refresh || refreshToken);

          // Retry original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access}`;
          }
          return apiClient(originalRequest);
        } catch (refreshError) {
          // Refresh failed - clear tokens and redirect to login
          clearTokens();
          window.dispatchEvent(new CustomEvent('auth:logout'));
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  },
);

// Error helper
export interface ApiError {
  message: string;
  field?: string;
  code?: string;
}

export const extractApiErrors = (error: AxiosError<any>): ApiError[] => {
  if (error.response?.data) {
    const data = error.response.data;

    // Backend format: { errors: [ { field, message } ] } (P24, offer, auth)
    if (Array.isArray(data.errors)) {
      const list = data.errors
        .filter((e: any) => e && (e.message != null || e.detail != null))
        .map((e: any) => ({
          field: e.field ?? undefined,
          message: String(e.message ?? e.detail ?? 'Błąd'),
          code: e.code,
        }));
      if (list.length > 0) return list;
    }

    // DRF validation: { fieldName: ["msg1"] } or nested { address: { province: ["msg"] } }
    if (typeof data === 'object') {
      const errors: ApiError[] = [];
      const collect = (obj: Record<string, unknown>, prefix = ''): void => {
        for (const [key, value] of Object.entries(obj)) {
          const fieldName = prefix ? `${prefix}.${key}` : key;
          if (Array.isArray(value)) {
            value.forEach((msg) => {
              if (typeof msg === 'object' && msg !== null) {
                const message =
                  'message' in msg ? String((msg as any).message) : JSON.stringify(msg);
                const code = 'code' in msg ? String((msg as any).code) : undefined;
                errors.push({ field: fieldName, message, code });
              } else {
                errors.push({ field: fieldName, message: String(msg) });
              }
            });
          } else if (typeof value === 'string') {
            errors.push({ field: fieldName, message: value });
          } else if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
            collect(value as Record<string, unknown>, fieldName);
          }
        }
      };
      collect(data as Record<string, unknown>);
      if (errors.length > 0) return errors;
    }

    if (data.detail) {
      return [{ message: typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail) }];
    }
  }

  const status = error.response?.status;
  const noResponse = !error.response;
  if (noResponse) {
    return [{ message: 'Brak odpowiedzi z serwera. Sprawdź połączenie i adres API (VITE_API_URL).' }];
  }
  if (status === 403) {
    return [{ message: 'Brak uprawnień do tej operacji.' }];
  }
  if (status === 502 || status === 503) {
    return [{ message: 'Serwis tymczasowo niedostępny. Spróbuj za chwilę.' }];
  }
  return [{ message: 'Wystąpił nieoczekiwany błąd.' }];
};

export default apiClient;
